import { WithLoading, dialogFunction } from 'common/components';

import {
  IXOneFooter,
  IXOneGrid,
  IXOneHeader,
  IXOneMessage,
  IXOneTotalFee,
  RIVIRBillingReview,
  useIXOneColumns,
} from '../ixone-credit-card';

import { ContentConfirmRequest } from '../ixone-section/IXOneCapture';

import { useMutateMakeProductActive, useValidateActiveProduct } from './hooks';
import {
  useCheckPaymentTerm,
  usePayNowSavedMemberCard,
} from '../cod-payment/hooks';

import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { productFullViewKeys, useVisibleModal } from 'hooks';
import {
  COD_PAYMENT_DESCRIPTION,
  COD_PAYMENT_TYPE_VIEW,
  CODChargeValidating,
} from '../cod-payment';
import { keys } from 'pages/company-profile/components/tabs/billing/queries';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';
import { transformChargeItemsForCOD } from '../cod-payment/utils';
import { useGetSavedMemberCard } from 'pages/company-profile/components/tabs/billing/hooks';

export const ActiveProductCharge = ({
  productIds,
  onCancel,
  isFullView,
  children,
}) => {
  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const { isLoading, passedProducts } = useValidateActiveProduct({
    productIds,
  });

  const totalFee = passedProducts.reduce((accumulator, product) => {
    return accumulator + product.fee;
  }, 0);

  const isZeroFee = !totalFee;

  const canBypassCharge = hasBypassProductInternalPermission || isZeroFee;

  const [
    visibleReviewBilling,
    { onModal: onModalReviewBilling, offModal: offModalReviewBilling },
  ] = useVisibleModal();

  const { isCODTerm, isInvoicedTerm } = useCheckPaymentTerm();

  const { columns } = useIXOneColumns({ isActiveProduct: true });
  const { hasCreditCard } = useGetSavedMemberCard();

  const filteredColumns = canBypassCharge
    ? columns.filter(
        (col) => col?.field !== 'statusCharge' && col?.field !== 'fee'
      )
    : columns;

  const refetchProductGrid = useDispatchReloadPage();

  const activeProductChargeMutation = useMutateMakeProductActive({
    onCancel,
    meta: {
      invalidates: isFullView
        ? [
            productFullViewKeys.itemData(passedProducts?.[0]?.productId),
            keys.allMemberBillingStatement(),
          ]
        : [keys.allMemberBillingStatement()],
    },
  });

  const codPayNow = usePayNowSavedMemberCard({ onCancel });

  const handleCODPayNow = async () => {
    let codParams = {
      totalAmount: totalFee,
      isSendToCapture: false,
      chargeItems: transformChargeItemsForCOD(passedProducts),
      chargeDescription:
        COD_PAYMENT_DESCRIPTION[COD_PAYMENT_TYPE_VIEW.activeProduct],
    };

    await codPayNow.mutateAsync(codParams);
  };

  const confirmRequest = async () => {
    const params = {
      productIds: passedProducts.map((product) => product.productId),
    };

    await activeProductChargeMutation.mutateAsync(params, {
      onSuccess: (response) => {
        if (response?.isSuccess) {
          if (
            totalFee === 0 &&
            hasCreditCard &&
            isCODTerm &&
            !hasBypassProductInternalPermission
          )
            handleCODPayNow();
          onCancel();
          refetchProductGrid();
        }
      },
    });
  };

  const handleMakeProductActive = () => {
    dialogFunction({
      type: 'warn',
      content: <ContentConfirmRequest onClick={onModalReviewBilling} />,
      okText: 'Agree',
      onOk: confirmRequest,
    });
  };

  /**
   * Determines the correct payment action when user clicks "Confirm Request".
   * Priority order matters — checked top to bottom.
   *
   * 1. No credit card + not invoiced term → open COD modal to force user to add a card first.
   * 2. Has bypass permission (BYPASS_PRODUCT_INTERNAL_MAINTENANCE), fee is zero, or invoiced term → activate product directly, no charge needed.
   * 3. COD term (with card, with fee) → open COD modal to charge the saved card.
   * 4. Default (non-COD, non-invoiced, has fee) → show confirmation dialog before activating.
   */
  const handleConfirmClick = (handleCODCharge) => {
    if (!hasCreditCard && !isInvoicedTerm) return handleCODCharge();
    if (canBypassCharge || isInvoicedTerm) return confirmRequest();
    if (isCODTerm) return handleCODCharge();
    handleMakeProductActive();
  };

  return (
    <CODChargeValidating
      fee={totalFee}
      typeView={COD_PAYMENT_TYPE_VIEW.activeProduct}
      confirmRequest={confirmRequest}
      chargeItems={passedProducts}
      onCancel={onCancel}
    >
      {({ handleCODCharge }) => {
        return (
          <WithLoading loading={isLoading}>
            <IXOneHeader style={{ minHeight: 110 }} />
            <IXOneMessage message='Please confirm your selection to have the following products made Public and accessible to Retail Members of RIVIR' />
            <IXOneGrid columns={filteredColumns} productList={passedProducts}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  marginTop: 4,
                }}
              >
                {children}
                {!canBypassCharge && <IXOneTotalFee fee={totalFee} />}
              </div>
            </IXOneGrid>
            <div style={{ paddingTop: 6 }}>
              <IXOneFooter
                onSave={() => handleConfirmClick(handleCODCharge)}
                loading={activeProductChargeMutation.isLoading}
                disabled={!passedProducts?.length}
                onCancel={onCancel}
              />
            </div>

            {visibleReviewBilling && (
              <RIVIRBillingReview
                visible={visibleReviewBilling}
                onCancel={offModalReviewBilling}
              />
            )}
          </WithLoading>
        );
      }}
    </CODChargeValidating>
  );
};
